<template>
  <div class="teacher-app-page on-print">
    <Toolbar />
    <v-container class="teacher-home" fluid>
      <header class="teacher-home__welcome">
        <div>
          <p class="teacher-page-eyebrow">{{ todayLabel }}</p>
          <h1>Welcome back, {{ firstName }}</h1>
          <p>Here’s what’s happening in your classroom today.</p>
        </div>
      </header>

      <section v-if="performance || performanceLoading" class="teacher-performance-card" aria-label="Attendance performance">
        <div v-if="performanceLoading" class="teacher-performance-card__loading">
          <v-progress-circular size="24" width="3" color="primary" indeterminate />
          <span>Calculating your attendance standing…</span>
        </div>
        <template v-else>
          <div class="teacher-performance-card__header">
            <div>
              <p class="teacher-page-eyebrow">Last {{ performance.days }} days</p>
              <h2>Attendance standing</h2>
            </div>
            <span class="teacher-performance-rank">#{{ performance.rank }} of {{ performance.teacherCount }}</span>
          </div>

          <div class="teacher-performance-card__score">
            <div class="teacher-performance-stars" :aria-label="`${performance.stars} out of 5 stars`">
              <v-icon v-for="star in 5" :key="star" size="21" :color="star <= performance.stars ? '#e2a42f' : '#dce3de'">
                {{ performanceStarIcon(star) }}
              </v-icon>
            </div>
            <div><strong>{{ performance.score }}</strong><span>/ 100</span></div>
          </div>
          <v-progress-linear :value="performance.score" height="7" rounded color="#e2a42f" background-color="#edf1ee" />

          <div class="teacher-performance-stats">
            <div><strong>{{ performance.sessions }}</strong><span>Sessions taken</span></div>
            <div><strong>{{ performance.completedSessions }}</strong><span>Fully completed</span></div>
            <div><strong>{{ performance.schoolAverage }}</strong><span>School average</span></div>
          </div>
          <p class="teacher-performance-card__note">
            Your score combines attendance consistency and complete student rosters. Administration can review this standing.
          </p>
        </template>
      </section>

      <section
        v-if="Teacher && Teacher.ClassRoomTeachers && Teacher.ClassRoomTeachers.length"
        class="teacher-class-picker"
      >
        <div class="teacher-class-picker__icon" aria-hidden="true">
          <v-icon color="#2f7d4a">mdi-google-classroom</v-icon>
        </div>
        <div class="teacher-class-picker__control">
          <label for="teacher-class-select">Viewing class</label>
          <v-select
            id="teacher-class-select"
            v-model="selectedClassRoom"
            :items="Teacher.ClassRoomTeachers"
            item-text="ClassRoom.name"
            return-object
            :menu-props="{ offsetY: true, nudgeBottom: 6 }"
            hide-details
            outlined
            dense
            aria-label="Select a class"
          />
        </div>
      </section>

      <div v-if="isLoading" class="teacher-loading-state" role="status">
        <v-progress-circular :size="42" :width="4" color="primary" indeterminate />
        <strong>Preparing your classroom</strong>
        <span>Please wait a moment.</span>
      </div>

      <template v-else>
        <section class="teacher-metrics" aria-label="Class summary">
          <article class="teacher-metric-card">
            <div class="teacher-metric-card__icon teacher-metric-card__icon--green">
              <v-icon color="#2f7d4a">mdi-account-group-outline</v-icon>
            </div>
            <div>
              <strong>{{ Students.length }}</strong>
              <span>{{ Students.length === 1 ? "Student" : "Students" }}</span>
            </div>
          </article>
          <article class="teacher-metric-card">
            <div class="teacher-metric-card__icon teacher-metric-card__icon--blue">
              <v-icon color="#3c6e9c">mdi-chart-donut</v-icon>
            </div>
            <div><strong>{{ averageAttendance }}%</strong><span>Attendance</span></div>
          </article>
        </section>

        <section class="teacher-quick-actions" aria-label="Quick actions">
          <button type="button" @click="takeAttendance">
            <span><v-icon color="#2f7d4a">mdi-account-check-outline</v-icon></span>
            <strong>Take attendance</strong>
            <v-icon size="18" color="#829087">mdi-chevron-right</v-icon>
          </button>
          <button type="button" @click="addStudent">
            <span><v-icon color="#2f7d4a">mdi-account-plus-outline</v-icon></span>
            <strong>Add a student</strong>
            <v-icon size="18" color="#829087">mdi-chevron-right</v-icon>
          </button>
        </section>

        <section class="teacher-student-card" v-if="Teacher">
          <div class="teacher-section-heading">
            <div><h2>Your students</h2><p>{{ selectedClassName }}</p></div>
            <button v-if="Students.length" type="button" @click="addStudent">Add student</button>
          </div>

          <div v-if="Students.length" class="teacher-student-list">
            <template v-for="item in sortedStudents">
              <button
                v-if="item.Student"
                :key="item.StudentId"
                type="button"
                class="teacher-student-row"
                @click="openStudent(item)"
              >
                <span class="teacher-student-row__avatar">{{ initials(item.Student.fullName) }}</span>
                <span class="teacher-student-row__copy">
                  <strong>{{ item.Student.fullName }}</strong>
                  <small v-if="item.Student.localId !== null && item.Student.localId !== undefined" class="teacher-student-row__id">#{{ item.Student.localId }}</small>
                  <small v-if="item.Student.Customer && item.Student.Customer.fatherName">
                    Parent: {{ item.Student.Customer.fatherName }}
                  </small>
                  <small v-else>View student profile</small>
                </span>
                <span class="teacher-student-row__attendance" :class="attendanceClass(item.per)">
                  {{ item.per || 0 }}%
                </span>
                <v-icon size="18" color="#9aa6a0">mdi-chevron-right</v-icon>
              </button>
            </template>
          </div>

          <div v-else class="teacher-empty-state">
            <span><v-icon size="28" color="#2f7d4a">mdi-account-plus-outline</v-icon></span>
            <h3>No students in this class yet</h3>
            <p>Add students to start taking attendance and tracking progress.</p>
            <v-btn color="primary" elevation="0" @click="addStudent">Add your first student</v-btn>
          </div>
        </section>
      </template>
    </v-container>
    <BottomNavigation />
  </div>
</template>

<script>
import teacherService from "@/modules/Teacher/service.js";
import classRoomService from "@/modules/ClassRoom/service.js";
import attService from "@/modules/Attendance/service.js";
import Toolbar from "@/modules/Layout/components/Toolbar.vue";
import BottomNavigation from "@/modules/Layout/components/BottomNavigation.vue";
import sortStudentsByName from "@/utils/sortStudentsByName.js";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "teacher-home",
  components: { BottomNavigation, Toolbar },
  data() {
    return {
      isLoading: true,
      Students: [],
      Attendance: [],
      performance: null,
      performanceLoading: false,
    };
  },
  async created() {
    document.title = "taleem - Home";
    this.loadData();
  },
  watch: {
    currentUser() {
      this.loadData();
    },
    async selectedClassRoom(val) {
      if (val) {
        await this.getAttendance();
        await this.getStudent();
      }
    },
  },
  computed: {
    ...mapGetters("global", ["currentUser"]),
    ...mapGetters("teacherLayout", ["getClassRoom", "getTeacherStore"]),
    selectedClassRoom: {
      get() { return this.getClassRoom; },
      set(val) { this.setClassRoom(val); },
    },
    Teacher: {
      get() { return this.getTeacherStore; },
      set(val) { this.setTeacher(val); },
    },
    firstName() {
      if (!this.currentUser || !this.currentUser.name) return "Teacher";
      return this.currentUser.name.trim().split(" ")[0];
    },
    todayLabel() {
      return this.$moment().format("dddd, MMMM D");
    },
    selectedClassName() {
      return this.selectedClassRoom && this.selectedClassRoom.ClassRoom
        ? this.selectedClassRoom.ClassRoom.name
        : "Selected class";
    },
    averageAttendance() {
      if (!this.Students.length) return 0;
      const total = this.Students.reduce((sum, student) => sum + (Number(student.per) || 0), 0);
      return Math.round(total / this.Students.length);
    },
    sortedStudents() {
      return sortStudentsByName(this.Students);
    },
  },
  methods: {
    ...mapActions("teacherLayout", ["setClassRoom", "setTeacher"]),
    initials(name) {
      if (!name) return "S";
      return name.split(" ").filter(Boolean).slice(0, 2).map(part => part.charAt(0)).join("").toUpperCase();
    },
    performanceStarIcon(star) {
      return star <= this.performance.stars ? "mdi-star" : "mdi-star-outline";
    },
    attendanceClass(value) {
      const attendance = Number(value) || 0;
      if (attendance >= 80) return "teacher-student-row__attendance--good";
      if (attendance >= 60) return "teacher-student-row__attendance--medium";
      return "teacher-student-row__attendance--low";
    },
    takeAttendance() {
      this.$router.push({ name: "class-room-attendance" });
    },
    async loadData() {
      if (this.currentUser && this.currentUser.Teacher) {
        await this.getTeacher();
        this.getTeacherPerformance();
        if (this.Teacher.ClassRoomTeachers.length) {
          if (!this.selectedClassRoom) this.selectedClassRoom = this.Teacher.ClassRoomTeachers[0];
          else {
            await this.getAttendance();
            await this.getStudent();
          }
        }
      }
    },
    setAttendance() {
      this.Attendance.forEach(row => {
        row.per = ((row.totalPresentAttendance + row.totalLateAttendance + row.totalLeaveAttendance) / row.totalAttendance) * 100;
        this.Students.forEach(student => {
          if (student.StudentId === row.id) {
            student.per = isNaN(parseInt(row.per)) ? 0 : parseInt(row.per);
            student.totalAttendance = row.totalAttendance;
            student.totalPresentAttendance = row.totalPresentAttendance;
            student.totalLateAttendance = row.totalLateAttendance;
            student.totalLeaveAttendance = row.totalLeaveAttendance;
            student.totalAbsentAttendance = row.totalAbsentAttendance;
          }
        });
      });
    },
    getAttendance() {
      const where = {
        start: this.$moment().startOf("month").toISOString(),
        end: this.$moment().endOf("month").toISOString(),
        ClassRoomId: this.selectedClassRoom.ClassRoomId,
      };
      return attService.attendanceInfo(where).then(response => {
        this.Attendance = response.data;
        return response;
      });
    },
    getStudent() {
      this.isLoading = true;
      return classRoomService
        .getAssignStudentList({ ClassRoomId: this.selectedClassRoom.ClassRoomId, includeClassStudent: true })
        .then(response => {
          this.Students = response.data;
          this.setAttendance();
          this.isLoading = false;
          return response;
        });
    },
    getTeacherPerformance() {
      this.performanceLoading = true;
      return attService.teacherPerformance({ TeacherId: this.currentUser.Teacher.id, days: 30 })
        .then(response => {
          this.performance = response.data;
          return response;
        })
        .catch(() => {
          this.performance = null;
        })
        .finally(() => {
          this.performanceLoading = false;
        });
    },
    addStudent() {
      this.$router.push({
        name: "add-student-to-class-room",
        params: { teacher: this.Teacher, selectedClassRoom: this.selectedClassRoom, students: this.Students },
      });
    },
    openStudent(item) {
      this.$router.push({ name: "student-profile", params: { student: item, selectedClassRoom: this.selectedClassRoom } });
    },
    getTeacher() {
      return teacherService.getById(this.currentUser.Teacher.id).then(response => {
        this.Teacher = response.data;
        this.isLoading = false;
        return response;
      });
    },
  },
};
</script>
